import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Zap, ArrowLeft, Users, MousePointer, DollarSign,
  Link2, ExternalLink, CheckCircle, Clock, XCircle, AlertCircle,
  BarChart3, Calendar, CreditCard, Target, Pause, Play, Archive, StopCircle, Trash2
} from "lucide-react";

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth();
  
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  const fetchData = useCallback(async () => {
    try {
      const [campaignRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, axiosConfig),
        axios.get(`${API}/business/campaigns/${campaignId}/analytics`, axiosConfig)
      ]);
      setCampaign(campaignRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setNotification({ type: "warning", message: "Payment verification taking longer. Please refresh." });
      setPaymentProcessing(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, axiosConfig);
      
      if (response.data.payment_status === "paid") {
        setNotification({ type: "success", message: "Budget added successfully!" });
        setPaymentProcessing(false);
        fetchData();
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (response.data.status === "expired") {
        setNotification({ type: "error", message: "Payment session expired." });
        setPaymentProcessing(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();

    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success" && sessionId) {
      setPaymentProcessing(true);
      setNotification({ type: "info", message: "Verifying payment..." });
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      setNotification({ type: "warning", message: "Payment was cancelled." });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData, searchParams, pollPaymentStatus]);

  const handleAddBudget = async (budgetType) => {
    try {
      setPaymentProcessing(true);
      const response = await axios.post(
        `${API}/payments/campaign-budget/checkout`,
        { 
          origin_url: window.location.origin,
          campaign_id: campaignId,
          budget_type: budgetType
        },
        axiosConfig
      );
      window.location.href = response.data.url;
    } catch (error) {
      setNotification({ type: "error", message: "Failed to start checkout." });
      setPaymentProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: "badge-success", icon: CheckCircle, label: "Active" },
      paused: { color: "badge-warning", icon: Clock, label: "Paused" },
      completed: { color: "badge-info", icon: CheckCircle, label: "Completed" },
      archived: { color: "bg-gray-500/20 text-gray-400 border border-gray-500/30", icon: Archive, label: "Archived" },
      cancelled: { color: "badge-purple", icon: XCircle, label: "Cancelled" },
      pending_review: { color: "badge-warning", icon: Clock, label: "Pending Review" },
      rejected: { color: "bg-red-500/20 text-red-400 border border-red-500/30", icon: XCircle, label: "Rejected" }
    };
    const badge = badges[status] || { color: "bg-gray-500/20 text-gray-400", icon: Clock, label: status || "Unknown" };
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <badge.icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const handleStatusUpdate = async (newStatus) => {
    setStatusLoading(true);
    try {
      await axios.put(
        `${API}/campaigns/${campaignId}/status`,
        { status: newStatus },
        axiosConfig
      );
      setCampaign(prev => ({ ...prev, status: newStatus }));
      const messages = {
        active: "Campaign is now active and accepting results.",
        paused: "Campaign paused. No new results will be tracked.",
        completed: "Campaign marked as complete. Results are preserved.",
        archived: "Campaign archived. Access it from your campaign history."
      };
      setNotification({ type: "success", message: messages[newStatus] || `Status updated to ${newStatus}` });
    } catch (error) {
      setNotification({ 
        type: "error", 
        message: error.response?.data?.detail || "Failed to update campaign status" 
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, axiosConfig);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      setNotification({ 
        type: "error", 
        message: error.response?.data?.detail || "Failed to delete campaign" 
      });
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Campaign not found</h2>
          <button onClick={() => navigate("/dashboard")} className="text-cyan-400">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060D]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            notification.type === "success" ? "bg-green-500/10 border border-green-500/30 text-green-400" :
            notification.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" :
            notification.type === "warning" ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400" :
            "bg-blue-500/10 border border-blue-500/30 text-blue-400"
          }`}>
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto hover:opacity-70">×</button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold font-['Unbounded'] text-white">
                {campaign.title}
              </h1>
              {getStatusBadge(campaign.status)}
              {campaign.goal && (
                <span className="px-3 py-1 rounded-full text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                  {campaign.goal === "customers" ? "Pay per Customer" 
                    : campaign.goal === "leads" ? "Pay per Lead" 
                    : "Pay per Click"}
                </span>
              )}
            </div>
            <p className="text-gray-400 max-w-2xl">{campaign.description}</p>
          </div>
          
          {/* Campaign Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {campaign.status === "active" && (
              <>
                <button
                  onClick={() => handleStatusUpdate("paused")}
                  disabled={statusLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20 transition-colors disabled:opacity-50"
                  data-testid="pause-campaign-btn"
                >
                  <Pause className="w-4 h-4" />
                  Pause
                </button>
                <button
                  onClick={() => handleStatusUpdate("completed")}
                  disabled={statusLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                  data-testid="complete-campaign-btn"
                >
                  <StopCircle className="w-4 h-4" />
                  Complete
                </button>
              </>
            )}
            {campaign.status === "paused" && (
              <>
                <button
                  onClick={() => handleStatusUpdate("active")}
                  disabled={statusLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                  data-testid="resume-campaign-btn"
                >
                  <Play className="w-4 h-4" />
                  Resume
                </button>
                <button
                  onClick={() => handleStatusUpdate("completed")}
                  disabled={statusLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                  data-testid="complete-campaign-btn"
                >
                  <StopCircle className="w-4 h-4" />
                  Complete
                </button>
              </>
            )}
            {(campaign.status === "completed" || campaign.status === "cancelled") && (
              <button
                onClick={() => handleStatusUpdate("archived")}
                disabled={statusLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-500/10 border border-gray-500/30 text-gray-400 hover:bg-gray-500/20 transition-colors disabled:opacity-50"
                data-testid="archive-campaign-btn"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>
            )}
            {campaign.status === "archived" && (
              <button
                onClick={() => handleStatusUpdate("active")}
                disabled={statusLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                data-testid="reactivate-campaign-btn"
              >
                <Play className="w-4 h-4" />
                Reactivate
              </button>
            )}
            {/* Delete Campaign Button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
              data-testid="delete-campaign-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="glass-card rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Delete Campaign</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete "<span className="font-semibold text-white">{campaign.title}</span>"? 
                All campaign data, analytics, and creator participation records will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                  className="flex-1 py-3 rounded-xl border border-white/20 text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCampaign}
                  disabled={deleteLoading}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-btn"
                >
                  {deleteLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Campaign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Goal & Budget Summary */}
        {(campaign.cost_per_result > 0 || campaign.budget_amount > 0) && (
          <div className="glass-card rounded-xl p-4 mb-6 flex items-center gap-4 border border-cyan-500/20">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <div className="text-white font-medium">
                {campaign.cost_per_result > 0 && `€${campaign.cost_per_result} per ${campaign.goal === "customers" ? "customer" : campaign.goal === "leads" ? "lead" : "click"}`}
                {campaign.cost_per_result > 0 && campaign.budget_amount > 0 && " · "}
                {campaign.budget_amount > 0 && `€${campaign.budget_amount} total budget`}
              </div>
              <div className="text-sm text-gray-400">You only pay when results are delivered.</div>
            </div>
          </div>
        )}

        {/* Stats Grid - Dynamic based on goal */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {(() => {
            const goalMetricLabel = campaign.goal === "customers" ? "Customers" 
              : campaign.goal === "leads" ? "Leads" 
              : "Clicks";
            const goalMetricValue = campaign.goal === "customers" ? (analytics?.total_conversions || 0)
              : campaign.goal === "leads" ? (analytics?.total_leads || 0)
              : (analytics?.total_clicks || 0);
            
            return [
              { label: "Active Creators", value: analytics?.total_participants || 0, icon: Users, color: "cyan" },
              { label: goalMetricLabel, value: goalMetricValue, icon: MousePointer, color: "blue" },
              { label: "Total Budget", value: `€${campaign.budget_amount || 0}`, icon: DollarSign, color: "purple" },
              { label: "Creator Pool", value: `€${campaign.creator_reward_pool?.toFixed(0) || 0}`, icon: BarChart3, color: "cyan" }
            ];
          })().map((stat) => (
            <div key={stat.label} className="glass-card rounded-xl p-6 card-hover">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                stat.color === "cyan" ? "bg-cyan-500/20" :
                stat.color === "blue" ? "bg-blue-500/20" : "bg-purple-500/20"
              }`}>
                <stat.icon className={`w-6 h-6 ${
                  stat.color === "cyan" ? "text-cyan-400" :
                  stat.color === "blue" ? "text-blue-400" : "text-purple-400"
                }`} />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Add Budget Section */}
        {campaign.budget_amount === 0 && (
          <div className="glass-card rounded-xl p-6 mb-8 border border-cyan-500/30 bg-gradient-to-r from-cyan-600/10 to-purple-600/10">
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
              Add Campaign Budget
            </h3>
            <p className="text-gray-400 mb-6">
              Incentivize creators with a reward budget. 80% goes directly to participating creators.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { type: "small", amount: 50 },
                { type: "medium", amount: 100 },
                { type: "large", amount: 300 },
                { type: "premium", amount: 1000 }
              ].map((budget) => (
                <button
                  key={budget.type}
                  onClick={() => handleAddBudget(budget.type)}
                  disabled={paymentProcessing}
                  data-testid={`add-budget-${budget.type}`}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/50 transition-all text-center disabled:opacity-50"
                >
                  <div className="text-2xl font-bold text-white">€{budget.amount}</div>
                  <div className="text-sm text-gray-400 capitalize">{budget.type}</div>
                  <div className="text-xs text-cyan-400 mt-1">€{budget.amount * 0.8} to creators</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Targeting */}
        <div className="glass-card rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">Targeting</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-gray-400 mb-2">Niches</div>
              <div className="flex flex-wrap gap-2">
                {campaign.target_niches?.map((niche, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    {niche}
                  </span>
                ))}
                {!campaign.target_niches?.length && <span className="text-gray-500">All niches</span>}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-2">Audience Types</div>
              <div className="flex flex-wrap gap-2">
                {campaign.target_audience_types?.map((type, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {type}
                  </span>
                ))}
                {!campaign.target_audience_types?.length && <span className="text-gray-500">All audiences</span>}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-2">Platforms</div>
              <div className="flex flex-wrap gap-2">
                {campaign.target_platforms?.map((platform, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                    {platform}
                  </span>
                ))}
                {!campaign.target_platforms?.length && <span className="text-gray-500">All platforms</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Participants / Active Creators */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-xl font-bold font-['Syne'] text-white">Active Creators</h3>
            <p className="text-sm text-gray-400 mt-1">Creators promoting your campaign</p>
          </div>
          
          {!analytics?.participants?.length ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">No creators yet</h4>
              <p className="text-gray-400">Creators will appear here once they accept your campaign</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-dark">
                <thead>
                  <tr>
                    <th className="px-6 py-4 text-left">Creator</th>
                    <th className="px-6 py-4 text-left">Platform</th>
                    <th className="px-6 py-4 text-left">Followers</th>
                    <th className="px-6 py-4 text-left">
                      {campaign.goal === "customers" ? "Customers" : campaign.goal === "leads" ? "Leads" : "Clicks"}
                    </th>
                    <th className="px-6 py-4 text-left">Post</th>
                    <th className="px-6 py-4 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.participants.map((p) => (
                    <tr key={p.participation_id || p.creator_id}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                            {(p.creator_name || "C").charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white font-medium">{p.creator_name || "Creator"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          {p.platform || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {p.followers ? (
                          p.followers >= 1000000 ? `${(p.followers / 1000000).toFixed(1)}M` :
                          p.followers >= 1000 ? `${(p.followers / 1000).toFixed(1)}K` :
                          p.followers
                        ) : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1.5 text-gray-300">
                          <MousePointer className="w-4 h-4 text-gray-500" />
                          {campaign.goal === "customers" ? (p.conversions || 0) : 
                           campaign.goal === "leads" ? (p.leads || 0) : 
                           (p.clicks || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {p.post_url ? (
                          <a
                            href={p.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </a>
                        ) : (
                          <span className="text-gray-500">Not submitted</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          p.post_verified ? "badge-success" :
                          p.post_url ? "badge-warning" : "badge-info"
                        }`}>
                          {p.post_verified ? "Verified" : p.post_url ? "Pending" : "Active"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;
